import { useEffect, useState } from "react";
import { api, formatErr, canWrite, COMPANY_LABELS, COMPANY_COLORS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ExportPdfButton } from "@/components/ExportPdfButton";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const MultiCheck = ({ f, form, setForm, testId }) => {
  const opts = typeof f.options === "function" ? f.options(form) : f.options;
  const sel = form[f.key] || [];
  return (
    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1.5 border rounded-sm p-2 min-h-8" data-testid={testId}>
      {opts.length === 0 && <span className="text-xs text-muted-foreground">{f.empty || "Nessuna opzione"}</span>}
      {opts.map((o) => (
        <label key={o.value} className="flex items-center gap-1.5 text-sm">
          <Checkbox checked={sel.includes(o.value)} data-testid={`${testId}-${o.value}`}
            onCheckedChange={(v) => setForm({ ...form, [f.key]: v ? [...sel, o.value] : sel.filter((x) => x !== o.value) })} />{o.label}
        </label>
      ))}
    </div>
  );
};

/**
 * Generic CRUD page used for Padroncini / Veicoli / Tessere / Clienti.
 * Props: title, resource (api path), fields (col defs), formFields, defaults
 */
export default function CrudPage({ title, resource, fields, formFields, defaults, prepareEdit, onSaved, groups }) {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, resource);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [group, setGroup] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/${resource}`, { params: { company } });
      setItems(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [company]); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    setForm({ ...defaults, ...(groups && group ? { [groups.key]: group } : {}), company: company === "all" ? "golmar" : company });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm(prepareEdit ? prepareEdit(it) : { ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/${resource}/${editing.id}`, form);
      else await api.post(`/${resource}`, form);
      toast.success(editing ? "Aggiornato" : "Creato");
      setOpen(false); load(); onSaved?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    try { await api.delete(`/${resource}/${id}`); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filtered = items.filter((it) =>
    (!groups || !group || it[groups.key] === group) &&
    (!q || Object.values(it).some((v) => String(v).toLowerCase().includes(q.toLowerCase())))
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-heading font-black">{title}</h1>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input placeholder="Cerca..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="h-8 pl-7 w-56" data-testid={`${resource}-search`} />
          </div>
          <ExportPdfButton resource={resource} params={{ company }} />
          {writable && <Button size="sm" onClick={openCreate} className="h-8 gap-1" data-testid={`${resource}-new-button`}>
            <Plus className="w-4 h-4" />Nuovo
          </Button>}
        </div>
      </div>

      {groups && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${resource}-groups`}>
          {[{ value: "", label: "Tutte" }, ...groups.options].map((o) => (
            <Button key={o.value || "all"} size="sm" variant={group === o.value ? "default" : "outline"} className="h-7"
              onClick={() => setGroup(o.value)} data-testid={`${resource}-group-${o.value || "tutte"}`}>
              {o.label} ({o.value ? items.filter((it) => it[groups.key] === o.value).length : items.length})
            </Button>
          ))}
        </div>
      )}

      <Card className="rounded-md overflow-hidden">
        <table className="autotrans-table w-full" data-testid={`${resource}-table`}>
          <thead>
            <tr>
              {company === "all" && <th>Azienda</th>}
              {fields.map((f) => <th key={f.key}>{f.label}</th>)}
              <th className="w-20">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={fields.length + 2} className="text-center text-muted-foreground py-6">Nessun record</td></tr>
            )}
            {filtered.map((it) => (
              <tr key={it.id} data-testid={`${resource}-row-${it.id}`}>
                {company === "all" && (
                  <td><Badge style={{ backgroundColor: COMPANY_COLORS[it.company], color: "white" }} className="h-5">{COMPANY_LABELS[it.company]}</Badge></td>
                )}
                {fields.map((f) => <td key={f.key}>{f.render ? f.render(it) : (it[f.key] ?? "—")}</td>)}
                <td>{writable && <>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(it)}
                          data-testid={`${resource}-edit-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del(it.id)}
                          data-testid={`${resource}-delete-${it.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col gap-3 p-5">
          <DialogHeader>
            <DialogTitle>{title} · {editing ? "modifica" : "nuovo inserimento"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-2 overflow-y-auto pr-1 -mr-1 flex-1 min-h-0">
            {formFields.map((f) => (
              <div key={f.key} className={f.full ? "col-span-2 md:col-span-3" : ""}>
                <Label className="text-xs font-heading uppercase tracking-wider">{f.label}</Label>
                {f.type === "multi" ? (
                  <MultiCheck f={f} form={form} setForm={setForm} testId={`${resource}-field-${f.key}`} />
                ) : f.type === "select" ? (
                  <Select value={form[f.key] || (f.clearable ? "__none" : "")} onValueChange={(v) => setForm({ ...form, [f.key]: v === "__none" ? null : v })}>
                    <SelectTrigger className="h-8 mt-1" data-testid={`${resource}-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {f.clearable && <SelectItem value="__none">{f.noneLabel || "— nessuno —"}</SelectItem>}
                      {(typeof f.options === "function" ? f.options(form) : f.options).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] ?? ""}
                         onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? parseFloat(e.target.value || 0) : e.target.value })}
                         className="h-8 mt-1" data-testid={`${resource}-field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter className="border-t pt-3 shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid={`${resource}-save-button`}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
